import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { router } from "@/app/router";
import AppProvider from "@/app/providers/AppProvider";
import { FetchError } from "@/shared/lib/fetcher";
import { toast } from "@/shared/stores/notificationStore";
import { AgentEventsProvider } from "@/features/system-integrations/components/AgentEventsProvider";
import { FileDeliveryNotification } from "@/features/distributed-search/components/FileDeliveryNotification";
import { AgenticStreamProvider } from "@/shared/core/transport/AgenticStreamProvider";
import { useAuthStore } from "@/shared/stores/authStore";
import { CUAConfirmationDialog } from "@/features/computer-use/components/CUAConfirmationDialog";
import { CUALoopDetectedBanner } from "@/features/computer-use/components/CUALoopDetectedBanner";
import { CUAConsentModal } from "@/features/computer-use/components/CUAConsentModal";
import { CUADevToolsPanel } from "@/features/computer-use/components/CUADevToolsPanel";

function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof FetchError) return error.status;
  return (error as { response?: { status?: number } })?.response?.status;
}

// Throttled so a burst of queries failing together (many widgets sharing
// one rate-limited backend) surfaces one toast, not one per query.
let last429ToastAt = 0;
function notifyRateLimited() {
  const now = Date.now();
  if (now - last429ToastAt < 10_000) return;
  last429ToastAt = now;
  toast.warning(
    "Hệ thống đang bận",
    "Quá nhiều yêu cầu trong thời gian ngắn — vui lòng thử lại sau ít phút."
  );
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (getErrorStatus(error) === 429) notifyRateLimited();
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        const status = getErrorStatus(error);
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * AgenticStreamConnector — reads auth state and gates the SSE connection.
 * Separate component so hooks can be called unconditionally inside AppProvider
 * context (where UserProvider / authStore is available).
 */
function AgenticStreamConnector({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  return (
    <AgenticStreamProvider enabled={isAuthenticated}>
      {children}
    </AgenticStreamProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        {/* TASK-AGT-SSE-001: AgenticStreamProvider — 1 user-scoped SSE connection for all sessions.
            Mounts inside AppProvider so auth context (UserProvider) is available.
            Only connects when user is authenticated. */}
        <AgenticStreamConnector>
          {/* TASK-SI-008: AgentEventsProvider listens SSE agent events at App root */}
          <AgentEventsProvider>
            <RouterProvider router={router} />
            {/* Distributed Search: file delivery toast — root level mount (TASK-UI-DSEARCH-ROUTER-2) */}
            <FileDeliveryNotification />
            {/* Computer-Use: confirmation dialog for write actions (TASK-UI-CU-002-1) */}
            <CUAConfirmationDialog />
            {/* Computer-Use: consent modal for first-time grant (TASK-UI-CU-005-1) */}
            <CUAConsentModal />
            {/* Computer-Use: loop detection critical banner (TASK-UI-CU-007-2) */}
            <CUALoopDetectedBanner />
          </AgentEventsProvider>
        </AgenticStreamConnector>
      </AppProvider>
      {/* Sonner toast portal — renders notifications from AgentEventsProvider */}
      <Toaster position="bottom-right" richColors closeButton />
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
      {/* CUA DevTools — only in DEV (TASK-UI-CU-012-1) */}
      <CUADevToolsPanel />
    </QueryClientProvider>
  );
}
